import { useState } from 'react';
import Modal from '@/components/Modals/Modal';
import { ButtonComponent } from '@/components/Button/Button';
import { FilterInputSelect } from './FilterInputSelect';
import { type CustomIconName } from '@/components/Icon/Icon';
import { type Icon as FeatherIcon } from 'react-feather';
import type { CSSProperties } from 'react';
import classes from './FilterSelect.module.css';
import { Box } from '@mantine/core';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useAppFlags } from '@/hooks/useAppFlags';

interface BaseFilterSelectProps {
  /**
   * Button contents
   */
  label: string;
  /**
   * Is filter active
   */
  isActive?: boolean;
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /**
   * Custom button styling
   */
  style?: CSSProperties;
  /**
   * Whether this is a filter select toggle button
   */
  isToggle?: boolean;
  /**
   * Selected options
   */
  selected?: string[];
  /**
   * Filter select options
   */
  options?: FilterOption[];
  /**
   * Filter select onSubmit action
   */
  onSubmit?: (selected: FilterOption[]) => void;
  /**
   * Filter select placeholder when there is no selection
   */
  placeholder?: string;
  /**
   * Show selected options in label
   */
  showSelectedLabel?: boolean;
  'data-testid'?: string;
}

type FilterSelectProps =
  | (BaseFilterSelectProps & {
      iconName?: CustomIconName;
      IconComponent?: never;
    })
  | (BaseFilterSelectProps & { IconComponent?: FeatherIcon; iconName?: never });

export const FilterSelect = ({
  label,
  iconName,
  IconComponent,
  showSelectedLabel = false,
  ...props
}: FilterSelectProps) => {
  const isDropdown = props.options;
  const isSmallScreen = useSmallScreen();
  const { flagShiftSelect } = useAppFlags();

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const getLabel = () => {
    if (!showSelectedLabel) {
      return label;
    }

    if (props.selected && props.selected.length > 0) {
      const firstSelectedLabel = props.options?.find((i) =>
        props.selected?.includes(i.value)
      );
      const selectedLabel = firstSelectedLabel?.label ?? props.selected[0];

      if (props.selected.length === 1) {
        return selectedLabel;
      }

      return `${selectedLabel} +${props.selected.length - 1}`;
    }

    return label;
  };

  const buttonId = `${getLabel().replace(/\s+/g, '-').toLowerCase()}`;

  const { options, selected } = props;
  const selectedValue =
    options?.filter((i) => selected?.includes(i.value)) ?? [];

  const filterInputSelect = options ? (
    <FilterInputSelect
      triggerButtonId={buttonId}
      value={selectedValue}
      options={options}
      onSubmit={(s: FilterOption[]) => {
        setDropdownOpen(false);
        if (props.onSubmit) {
          props.onSubmit(s);
        }
      }}
      allowShiftSelect={flagShiftSelect}
    />
  ) : null;

  return (
    <div className={classes.container} data-testid={props['data-testid']}>
      <ButtonComponent
        type="button"
        id={buttonId}
        variant="filter"
        size="sm"
        label={getLabel()}
        isSelected={props.isActive}
        leftIconName={iconName}
        leftIconComponent={IconComponent}
        isDropDown={props.options && props.options?.length > 0}
        isToggle={props.isToggle}
        onClick={() => {
          if (isDropdown) {
            setDropdownOpen((o) => !o);
          } else if (props.onClick) {
            props.onClick();
          }
        }}
      />
      {filterInputSelect && isSmallScreen && (
        <Modal
          opened={isDropdownOpen}
          title={getLabel()}
          onClose={() => {
            setDropdownOpen(false);
          }}
        >
          {filterInputSelect}
        </Modal>
      )}
      {filterInputSelect && isDropdownOpen && (
        <Box visibleFrom="md">{filterInputSelect}</Box>
      )}
    </div>
  );
};

export interface FilterOption {
  value: string;
  label: string;
  subLabel?: string;
}

export interface FilterSelectArgs {
  options: FilterOption[];
  selected: string[];
  onSubmit: (s: FilterOption[]) => void;
}
